import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Card } from '../../../components/ui/card';
import { BellRing, Loader2, GraduationCap, UserCheck, AlertTriangle } from 'lucide-react';
import { fmtDate } from './demoConstants';

const ago = (iso) => {
  if (!iso) return null;
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/** Manager tool: one click pings everyone sitting on pending demo feedback. */
export default function NudgeFeedbackDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    try {
      const r = await api.get('/training/demos/pending-feedback');
      setData(r.data);
      const next = {};
      (r.data.groups || []).forEach((g) => { next[g.key] = g.can_nudge; });
      setSel(next);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load pending feedback'); onClose?.(); }
  }, [onClose]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const groups = data?.groups || [];
  const chosen = groups.filter((g) => sel[g.key] && g.can_nudge);

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post('/training/demos/nudge', { keys: chosen.map((g) => g.key) });
      toast.success(`Nudged ${r.data.sent} ${r.data.sent === 1 ? 'person' : 'people'}${r.data.skipped ? ` · ${r.data.skipped} skipped (cooling down)` : ''}`);
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Nudge failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col bg-white" data-testid="nudge-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><BellRing className="w-4 h-4 text-violet-600" />Nudge pending demo feedback</DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500">
            In-app reminder to everyone whose feedback is still missing on a demo that has already happened.
            Someone nudged in the last {data?.cooldown_hours || 12} hours is skipped.
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Checking who's pending…</div>
        ) : groups.length === 0 ? (
          <div className="py-14 text-center" data-testid="nudge-empty">
            <p className="text-sm font-semibold text-slate-700">Nothing pending</p>
            <p className="text-[12px] text-slate-400 mt-1">Every demo that has happened already has both feedbacks.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="nudge-totals">
              {[
                { l: 'People to nudge', v: data.totals.people },
                { l: 'Trainer feedback', v: data.totals.trainer_pending },
                { l: 'Counsellor feedback', v: data.totals.counsellor_pending },
                { l: 'Pending > 3 days', v: data.totals.stale, red: true },
              ].map((x) => (
                <Card key={x.l} className="p-3 border">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{x.l}</p>
                  <p className={`text-xl font-bold tabular-nums ${x.red && x.v ? 'text-rose-600' : 'text-slate-900'}`}>{x.v}</p>
                </Card>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto mt-3 space-y-2 pr-1">
              {groups.map((g) => (
                <Card key={g.key} className={`p-3.5 border ${!g.can_nudge ? 'opacity-60' : ''}`} data-testid={`nudge-row-${g.key}`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      className="mt-0.5"
                      disabled={!g.can_nudge}
                      checked={!!sel[g.key] && g.can_nudge}
                      onCheckedChange={(v) => setSel((p) => ({ ...p, [g.key]: !!v }))}
                      data-testid={`nudge-check-${g.key}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                        <span className={`w-6 h-6 rounded-md flex items-center justify-center ${g.side === 'trainer' ? 'bg-violet-100 text-violet-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {g.side === 'trainer' ? <GraduationCap className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                        </span>
                        <span className="text-[13px] font-semibold text-slate-900">{g.person_name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{g.side}</span>
                        <span className="text-[11.5px] text-slate-500">{g.count} demo{g.count > 1 ? 's' : ''} pending · oldest {g.oldest_days}d</span>
                        {g.stale_count > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                            <AlertTriangle className="w-2.5 h-2.5" />{g.stale_count} over 3 days
                          </span>
                        )}
                        {!g.can_nudge && <span className="text-[10.5px] text-amber-600">nudged {ago(g.last_nudged_at)} — cooling down</span>}
                      </div>
                      <p className="text-[11.5px] text-slate-500 mt-1.5 truncate">
                        {g.demos.slice(0, 5).map((d) => `${d.demo_code} (${fmtDate(d.demo_date)})`).join(' · ')}
                        {g.demos.length > 5 ? ` +${g.demos.length - 5} more` : ''}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onClose?.()} data-testid="nudge-cancel">Close</Button>
          <Button
            onClick={send}
            disabled={busy || chosen.length === 0}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="nudge-send"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Sending…</> : <><BellRing className="w-4 h-4 mr-1.5" />Send {chosen.length || ''} reminder{chosen.length === 1 ? '' : 's'}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
